import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Receipt,
  Search, 
  Filter,
  Eye,
  Edit
} from 'lucide-react';
import { invoicesAPI, receiptsAPI, clientsAPI } from '../services/api';

interface Client {
  _id: string;
  clientName: string;
  email: string;
  contactNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
  };
  gstin?: string;
  placeOfSupply: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  client: Client;
  totalAmount: number;
  status: string;
}

interface ReceiptData {
  _id: string;
  receiptNumber: string;
  receiptDate: string;
  invoiceNumber: string;
  client: Client;
  amount: number;
  paymentMode: string;
}

const RevisedInvoicesReceipts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'receipts'>('invoices');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receipts, setReceipts] = useState<ReceiptData[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'invoices') {
        fetchInvoices();
      } else {
        fetchReceipts();
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedClient, selectedStatus, activeTab]);

  const fetchData = async () => {
    try {
      await Promise.all([
        fetchInvoices(),
        fetchReceipts(),
        fetchClients()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const params: any = {
        limit: 1000,
        search: searchTerm || undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined
      };
      
      if (selectedClient) {
        params.clientId = selectedClient;
      }

      const response = await invoicesAPI.getAll(params);
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  };

  const fetchReceipts = async () => {
    try {
      const params: any = {
        limit: 1000,
        search: searchTerm || undefined
      };
      
      if (selectedClient) {
        params.clientId = selectedClient;
      }

      const response = await receiptsAPI.getAll(params);
      setReceipts(response.data.receipts || []);
    } catch (error) {
      console.error('Error fetching receipts:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({ limit: 1000 });
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const handleEdit = (id: string) => {
    if (activeTab === 'invoices') {
      navigate(`/revised/invoice/${id}`);
    } else {
      navigate(`/revised/receipt/${id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Edit className="h-8 w-8 text-primary-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Revised Invoice & Receipt</h1>
            <p className="text-gray-600">Edit and revise your invoices and receipts</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => {
              setActiveTab('invoices');
              setSearchTerm('');
              setSelectedClient('');
              setSelectedStatus('all');
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invoices'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <FileText className="h-5 w-5" />
              <span>Invoices</span>
            </div>
          </button>
          <button
            onClick={() => {
              setActiveTab('receipts');
              setSearchTerm('');
              setSelectedClient('');
              setSelectedStatus('all');
            }}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'receipts'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Receipt className="h-5 w-5" />
              <span>Receipts</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={activeTab === 'invoices' ? 'Search invoices...' : 'Search receipts...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>

          {/* Client Filter */}
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Clients</option>
              {clients.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.clientName}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter - Only for Invoices */}
          {activeTab === 'invoices' && (
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="all">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Sent">Sent</option>
              <option value="Overdue">Overdue</option>
              <option value="Draft">Draft</option>
            </select>
          )}
        </div>
      </motion.div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card"
      >
        {activeTab === 'invoices' ? (
          // Invoices List
          invoices.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No invoices found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Invoice No.</th>
                    <th className="table-header">Client</th>
                    <th className="table-header">Date</th>
                    <th className="table-header">Due Date</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice._id} className="hover:bg-gray-50">
                      <td className="table-cell font-medium">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.client.clientName}</p>
                          <p className="text-sm text-gray-500">{invoice.client.email}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        {new Date(invoice.invoiceDate).toLocaleDateString('en-IN')}
                      </td>
                      <td className="table-cell">
                        {new Date(invoice.dueDate).toLocaleDateString('en-IN')}
                      </td>
                      <td className="table-cell font-medium">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                          invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                          invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {invoice.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <button
                          onClick={() => handleEdit(invoice._id)}
                          className="text-primary-600 hover:text-primary-700 p-2 hover:bg-primary-50 rounded-lg transition-colors flex items-center space-x-2"
                          title="Edit invoice"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          // Receipts List
          receipts.length === 0 ? (
            <div className="text-center py-8">
              <Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No receipts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="table-header">Receipt No.</th>
                    <th className="table-header">Invoice No.</th>
                    <th className="table-header">Client</th>
                    <th className="table-header">Date</th>
                    <th className="table-header">Amount</th>
                    <th className="table-header">Payment Mode</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {receipts.map((receipt) => (
                    <tr key={receipt._id} className="hover:bg-gray-50">
                      <td className="table-cell font-medium">
                        {receipt.receiptNumber}
                      </td>
                      <td className="table-cell">
                        {receipt.invoiceNumber}
                      </td>
                      <td className="table-cell">
                        <div>
                          <p className="font-medium text-gray-900">{receipt.client.clientName}</p>
                          <p className="text-sm text-gray-500">{receipt.client.email}</p>
                        </div>
                      </td>
                      <td className="table-cell">
                        {new Date(receipt.receiptDate).toLocaleDateString('en-IN')}
                      </td>
                      <td className="table-cell font-medium">
                        {formatCurrency(receipt.amount)}
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                          receipt.paymentMode === 'Cash' ? 'bg-green-100 text-green-800' :
                          receipt.paymentMode === 'Bank Transfer' ? 'bg-purple-100 text-purple-800' :
                          receipt.paymentMode === 'UPI' ? 'bg-orange-100 text-orange-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {receipt.paymentMode}
                        </span>
                      </td>
                      <td className="table-cell">
                        <button
                          onClick={() => handleEdit(receipt._id)}
                          className="text-primary-600 hover:text-primary-700 p-2 hover:bg-primary-50 rounded-lg transition-colors flex items-center space-x-2"
                          title="Edit receipt"
                        >
                          <Edit className="h-4 w-4" />
                          <span>Edit</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </motion.div>
    </div>
  );
};

export default RevisedInvoicesReceipts;

